import {
  BlockStack,
  TextField,
  Select,
  Checkbox,
  Text,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { AttendeeDetail, Country } from "@features/events/types";
import { useMemo } from "react";

interface CompanyInfoCardProps {
  attendee?: Partial<AttendeeDetail>;
  countries: Country[];
  onDataChange: (updates: Partial<AttendeeDetail>) => void;
  loading: boolean;
  fieldErrors?: Record<string, string>;
}

export default function CompanyInfoCard({
  attendee,
  countries,
  onDataChange,
  loading,
  fieldErrors = {},
}: CompanyInfoCardProps) {
  const { t } = useTranslation();

  const countryOptions = useMemo(
    () =>
      (countries || []).map((country) => ({
        label: country?.name || "Unknown Country",
        value: country?.iso3 || "",
      })),
    [countries]
  );

  if (loading) {
    return <SkeletonBodyText lines={4} />;
  }

  return (
    <BlockStack gap="400">
      <Text variant="headingMd" as="h2">
        {t("attendeeDetails.companyInfo.title")}
      </Text>

      <Checkbox
        label={t("attendeeDetails.companyInfo.isRegistering")}
        checked={attendee?.isCompany || false}
        onChange={(checked) => onDataChange({ isCompany: checked })}
      />

      {attendee?.isCompany && (
        <BlockStack gap="400">
          <TextField
            label={t("attendeeDetails.companyInfo.companyName")}
            value={attendee?.companyName || ""}
            onChange={(value) => onDataChange({ companyName: value })}
            autoComplete="organization"
            error={fieldErrors.companyName}
          />

          <TextField
            label={t("attendeeDetails.companyInfo.companyTaxId")}
            value={attendee?.companyTaxId || ""}
            onChange={(value) => onDataChange({ companyTaxId: value })}
            autoComplete="off"
            error={fieldErrors.companyTaxId}
          />

          <TextField
            label={t("attendeeDetails.companyInfo.companyAddress")}
            value={attendee?.companyAddress || ""}
            onChange={(value) => onDataChange({ companyAddress: value })}
            multiline={2}
            autoComplete="street-address"
            error={fieldErrors.companyAddress}
          />

          <Select
            label={t("attendeeDetails.companyInfo.companyCountry")}
            options={countryOptions}
            value={attendee?.companyCountry || "BGR"}
            onChange={(value) =>
              onDataChange({ companyCountry: value ?? undefined })
            }
            error={fieldErrors.companyCountry}
          />
        </BlockStack>
      )}
    </BlockStack>
  );
}
